import { FORM_ERROR, setIn } from 'final-form';
import { ValidationError, ObjectSchema, setLocale } from 'yup';
import { ObjectShape } from 'yup/lib/object';
import { MessageParams } from 'yup/lib/types';

type PrimitiveType = string | number | boolean | null | undefined | Date;

type SchemaError =
  | {
      id: string;
      values?: Record<string, PrimitiveType>;
    }
  | string;

type LocalizableErrorObject = Record<
  string,
  SchemaError | ((params: MessageParams & Record<string, any>) => SchemaError)
>;

export const defaultDictionary: Record<string, LocalizableErrorObject> = {
  // eslint-disable-next-line id-blacklist
  string: {
    max: ({ max }) => ({ id: 'validation.stringMax', values: { max } }),
    url: { id: 'validation.mustBeValidUrl' },
  },
  number: {
    integer: { id: 'validation.mustBeInteger' },
    min: ({ min }) => ({ id: 'validation.mustBeGreaterOrEqualThanMin', values: { min } }),
    max: ({ max }) => ({ id: 'validation.mustBeLessOrEqualThanMax', values: { max } }),
    lessThan: ({ less }) => ({ id: 'validation.mustBeLessThan', values: { less } }),
    positive: { id: 'validation.mustBePositive' },
  },
  mixed: {
    required: { id: 'validation.required' },
    oneOf: ({ values }) => ({ id: 'validation.mustBeOneOf', values: { values } }),
    notType: params => {
      switch (params.type) {
        case 'number':
          return { id: 'validation.mustBeNumber' };
        default:
          return `Type error not defined for '${params.type}'! The value received was '${params.value}'`;
      }
    },
  },
};

setLocale(defaultDictionary);

const validateYupSchema =
  <T extends ObjectShape>(schema: ObjectSchema<T>, context: object = {}) =>
  (values: any) => {
    try {
      schema.validateSync(values, { abortEarly: false, context });
      return;
    } catch (error) {
      if (error instanceof ValidationError) {
        return error.inner.reduce((acc, { path, message }) => {
          return setIn(acc, path || FORM_ERROR, message);
        }, {});
      }
      throw new Error(error);
    }
  };

export default validateYupSchema;
